import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useLang } from "@/context/LangContext";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Globe, List, X, SquaresFour, SignOut, House } from "@phosphor-icons/react";

const HOME_LABEL = {
  en: "Home", nl: "Home", pl: "Główna", it: "Home", sk: "Domov", es: "Inicio", pt: "Início",
};

const linkCls = "flex items-center gap-1.5 text-sm text-ppc-muted transition-colors hover:text-primary-green";

export const Navbar = () => {
  const { t, lang, setLang, languages } = useLang();
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);

  const currentLang = languages.find((l) => l.code === lang);

  const handleLogout = async () => {
    await logout();
    navigate("/");
  };

  const links = (
    <>
      <Link to="/home" onClick={() => setOpen(false)} data-testid="nav-home-link" className={linkCls}>
        <House size={16} /> {HOME_LABEL[lang] || HOME_LABEL.en}
      </Link>
      <Link to="/how-it-works" onClick={() => setOpen(false)} data-testid="nav-how-link" className={linkCls}>
        {t("nav_how")}
      </Link>
      <Link to="/browse" onClick={() => setOpen(false)} data-testid="nav-browse-link" className={linkCls}>
        {t("nav_browse")}
      </Link>
      <Link to="/create-listing" onClick={() => setOpen(false)} data-testid="nav-provider-link" className={linkCls}>
        {t("nav_provider")}
      </Link>
    </>
  );

  return (
    <header className="glass-nav sticky top-0 z-50">
      <nav className="mx-auto flex max-w-7xl items-center justify-between px-5 py-3 lg:px-8">
        <Link to="/home" data-testid="nav-logo" aria-label="PlinPlinCare home" className="flex items-center">
          <img src="/plinplincare-wordmark-dark.png" alt="PlinPlinCare" className="h-5 w-auto object-contain sm:h-6" />
        </Link>

        <div className="hidden items-center gap-8 md:flex">{links}</div>

        <div className="flex items-center gap-2">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="sm" data-testid="lang-switcher-btn" className="btn-secondary gap-1.5 rounded-xl">
                <Globe size={16} color="#9184d9" />
                <span className="hidden sm:inline">{currentLang?.flag}</span>
                <span className="text-xs uppercase">{lang}</span>
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              {languages.map((l) => (
                <DropdownMenuItem
                  key={l.code}
                  data-testid={`lang-option-${l.code}`}
                  onClick={() => setLang(l.code)}
                  className="cursor-pointer gap-2"
                >
                  <span>{l.flag}</span> {l.label}
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>

          {user ? (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button
                  data-testid="nav-user-menu"
                  className="avatar-grad grid h-9 w-9 place-items-center rounded-xl text-sm font-medium"
                  aria-label={user.name}
                >
                  {(user.name || user.email || "U")[0].toUpperCase()}
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem data-testid="nav-dashboard-link" onClick={() => navigate("/dashboard")} className="cursor-pointer gap-2">
                  <SquaresFour size={16} /> {t("nav_dashboard")}
                </DropdownMenuItem>
                <DropdownMenuItem data-testid="nav-logout-btn" onClick={handleLogout} className="cursor-pointer gap-2">
                  <SignOut size={16} /> {t("nav_logout")}
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          ) : (
            <div className="hidden items-center gap-2 md:flex">
              <Button variant="ghost" size="sm" data-testid="nav-login-btn" onClick={() => navigate("/login")} className="rounded-xl text-primary-green hover:bg-white/5">
                {t("nav_login")}
              </Button>
              <Button size="sm" data-testid="nav-register-btn" onClick={() => navigate("/register")} className="btn-primary rounded-xl px-4">
                {t("nav_register")}
              </Button>
            </div>
          )}

          <button className="md:hidden" data-testid="nav-mobile-toggle" onClick={() => setOpen(!open)} aria-label="Menu">
            {open ? <X size={24} /> : <List size={24} />}
          </button>
        </div>
      </nav>

      {open && (
        <>
          <button
            type="button"
            aria-label="close menu"
            onClick={() => setOpen(false)}
            className="fixed inset-0 top-[57px] z-40 bg-black/30 md:hidden"
            data-testid="nav-mobile-overlay"
          />
          <div
            className="nav-drop absolute inset-x-3 top-[calc(100%+8px)] z-50 flex flex-col gap-4 p-5 md:hidden"
            data-testid="nav-mobile-menu"
          >
            {links}
            {!user && (
              <div className="flex gap-2">
                <Button variant="outline" data-testid="nav-login-btn-mobile" onClick={() => { setOpen(false); navigate("/login"); }} className="btn-secondary flex-1 rounded-xl">
                  {t("nav_login")}
                </Button>
                <Button data-testid="nav-register-btn-mobile" onClick={() => { setOpen(false); navigate("/register"); }} className="btn-primary flex-1 rounded-xl">
                  {t("nav_register")}
                </Button>
              </div>
            )}
          </div>
        </>
      )}
    </header>
  );
};
